import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ArchiveUserDialog from "@/components/users/ArchiveUserDialog";
import CreateUserDialog from "@/components/users/CreateUserDialog";
import EditRoleDialog from "@/components/users/EditRoleDialog";
import ImpersonateUserDialog from "@/components/users/ImpersonateUserDialog";
import RoleChip from "@/components/users/RoleChip";
import UserActions from "@/components/users/UserActions";
import { COL_HEADER_SX, FILTER_SELECT_SX, PAGE_LIMIT, TABLE_COLS } from "@/constants/users";
import { usePermissions } from "@/hooks/usePermissions";
import { useRoles } from "@/hooks/useRoles";
import { useUsers } from "@/hooks/useUsers";
import type { User } from "@/interfaces/user";
import type { Role } from "@/lib/roles";
import { resolveRoleLabels } from "@/utils/role-label-helper";

export default function Users() {
  const { can } = usePermissions();
  const { roles } = useRoles();
  const canEdit = can("edit", "USER");
  const canCreate = can("create", "USER");

  // Two roles can share a label (e.g. VIEW_ONLY and ORG_VIEW_ONLY both read "View Only").
  // Qualify only the labels that actually collide within this dropdown so an unambiguous
  // rename never needs a code change here. This filter is read-only (it never gates access),
  // but an unlabeled duplicate still leaves the user unable to tell which role they picked.
  const roleDisplayLabel = resolveRoleLabels(roles);
  const roleFilterOptions: { value: Role | "all"; label: string }[] = [
    { value: "all", label: "All Roles" },
    ...roles.map((r) => ({ value: r.key, label: roleDisplayLabel(r.key) })),
  ];

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<Role | "all">("all");
  const [page, setPage] = useState(1);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [archivingUser, setArchivingUser] = useState<User | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [impersonatingUser, setImpersonatingUser] = useState<User | null>(null);

  // Debounce search — same pattern as Campaigns.tsx
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: response, isLoading } = useUsers({
    search: debouncedSearch || undefined,
    roles: roleFilter !== "all" ? [roleFilter] : undefined,
    page,
    limit: PAGE_LIMIT,
  });

  const users = response?.data ?? [];
  const total = response?.total ?? 0;
  const totalPages = response?.totalPages ?? 1;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 112px)",
        overflow: "hidden",
      }}
    >
      {/* Sub-header — matches Campaigns.tsx layout exactly */}
      <Box sx={{ flexShrink: 0, bgcolor: "var(--color-background)", px: 3, pb: 2 }}>
        <Typography variant="body1" sx={{ color: "var(--color-rodeo-black)", mb: 2.5 }}>
          Users ({isLoading ? "…" : total})
        </Typography>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <TextField
            placeholder="Search by email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--color-graphite)", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 360,
              "& .MuiOutlinedInput-root": {
                height: 52,
                borderRadius: "var(--radius-btn)",
                bgcolor: "background.paper",
                "& fieldset": { borderColor: "var(--color-smoke)" },
                "&:hover fieldset": { borderColor: "var(--color-rodeo-dust)" },
                "&.Mui-focused fieldset": { borderColor: "var(--color-dark-dust)" },
              },
              "& .MuiInputBase-input": {
                fontFamily: "var(--font-lato)",
                fontSize: 16,
                color: "var(--color-rodeo-black)",
                "&::placeholder": { color: "var(--color-graphite)", opacity: 1 },
              },
            }}
          />
          <Select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as Role | "all");
              setPage(1);
            }}
            displayEmpty
            IconComponent={ExpandMoreIcon}
            sx={{ ...FILTER_SELECT_SX, flex: "0 0 220px" }}
          >
            {roleFilterOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                sx={{
                  fontFamily: "var(--font-lato)",
                  fontSize: 14,
                  color: "var(--color-rodeo-black)",
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title={canCreate ? "Invite user" : "You don't have permission to create users"}>
            <span>
              <IconButton
                disabled={!canCreate}
                onClick={() => setCreateOpen(true)}
                size="small"
                sx={{
                  width: 52,
                  height: 52,
                  border: "1px solid var(--color-smoke)",
                  borderRadius: "var(--radius-btn)",
                  color: "var(--color-graphite)",
                  "&:hover": { bgcolor: "var(--color-wax)", color: "var(--color-rodeo-black)" },
                  "&.Mui-disabled": {
                    color: "var(--color-smoke)",
                    borderColor: "var(--color-wax)",
                  },
                }}
              >
                <PersonAddAlt1Icon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Table area — scrollable, sticky header, CSS grid like BouncedEmailsModal */}
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, px: 3 }}>
        {/* Column headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 52px", md: TABLE_COLS },
            py: 1.5,
            gap: 2,
            alignItems: "center",
            borderBottom: "1px solid var(--color-smoke)",
            position: "sticky",
            top: 0,
            bgcolor: "var(--color-background)",
            zIndex: 1,
          }}
        >
          <Typography sx={COL_HEADER_SX}>Name</Typography>
          <Typography sx={{ ...COL_HEADER_SX, display: { xs: "none", md: "block" } }}>
            Email
          </Typography>
          <Typography sx={{ ...COL_HEADER_SX, display: { xs: "none", md: "block" } }}>
            Group / Client
          </Typography>
          <Typography sx={{ ...COL_HEADER_SX, display: { xs: "none", md: "block" } }}>
            Role
          </Typography>
          <Box />
        </Box>

        {/* Loading state */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress
              aria-label="Loading"
              size={32}
              sx={{ color: "var(--color-dark-dust)" }}
            />
          </Box>
        ) : users.length === 0 ? (
          /* Empty state */
          <Box
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 1 }}
          >
            <Typography
              variant="h4"
              sx={{ color: "var(--color-graphite)", fontFamily: "var(--font-crimson)" }}
            >
              No users found
            </Typography>
            <Typography variant="body2" sx={{ color: "var(--color-graphite)" }}>
              {debouncedSearch || roleFilter !== "all"
                ? "Try adjusting your search or filter."
                : "No users are currently registered."}
            </Typography>
          </Box>
        ) : (
          /* Data rows */
          users.map((user) => (
            <Box
              key={user.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 52px", md: TABLE_COLS },
                py: 2,
                gap: 2,
                alignItems: "center",
                borderBottom: "1px solid var(--color-smoke)",
                "&:hover": { bgcolor: "var(--color-clear-dust)" },
              }}
            >
              <Box>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "var(--color-rodeo-black)",
                      lineHeight: 1.3,
                    }}
                  >
                    {user.name}
                  </Typography>
                  {user.verified === false && (
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        bgcolor: "var(--color-wax)",
                        color: "var(--color-graphite)",
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      Pending
                    </Typography>
                  )}
                </Stack>
                {/* Email visible on mobile under name */}
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "var(--color-graphite)",
                    display: { xs: "block", md: "none" },
                    mt: 0.25,
                  }}
                >
                  {user.email}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "var(--color-rodeo-black)",
                  display: { xs: "none", md: "block" },
                  wordBreak: "break-word",
                }}
              >
                {user.email}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "var(--color-graphite)",
                  display: { xs: "none", md: "block" },
                }}
              >
                {user.group ?? "—"}
              </Typography>
              <Box sx={{ display: { xs: "none", md: "flex" } }}>
                {(() => {
                  const r = user.orgRole ?? user.gfgRole;
                  return r ? <RoleChip role={r} /> : null;
                })()}
              </Box>
              <UserActions
                user={user}
                onImpersonate={setImpersonatingUser}
                onEditRole={setEditingUser}
                onArchive={setArchivingUser}
              />
            </Box>
          ))
        )}
      </Box>

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 2,
            borderTop: "1px solid var(--color-smoke)",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "var(--font-lato)",
                color: "var(--color-graphite)",
                "&.Mui-selected": { bgcolor: "var(--color-dark-dust)", color: "white" },
                "&:hover": { bgcolor: "var(--color-light-dust)" },
              },
            }}
          />
        </Box>
      )}

      <EditRoleDialog user={editingUser} onClose={() => setEditingUser(null)} canEdit={canEdit} />

      <ArchiveUserDialog user={archivingUser} onClose={() => setArchivingUser(null)} />

      {/* Create user dialog */}
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <ImpersonateUserDialog user={impersonatingUser} onClose={() => setImpersonatingUser(null)} />
    </Box>
  );
}
